import { Link } from "react-router-dom";
import { Star, MapPin, TrendingDown, ArrowUpRight, Check } from "lucide-react";

const BRAND_COLOR = {
  "Booking.com": "#003580", "Booking.com Preferred": "#003580",
  "Hotels.com": "#D32F2F", "Hotels.com Collection": "#D32F2F",
  "Expedia": "#FFC72C", "Priceline": "#00426A", "Agoda": "#F00A40",
  "Marriott Bonvoy": "#A89968", "Marriott": "#A89968",
  "Hilton Honors": "#10274C", "Hilton": "#10274C",
  "IHG One Rewards": "#004F97", "IHG": "#004F97",
  "World of Hyatt": "#00415E", "Hyatt": "#00415E",
  "Wyndham Rewards": "#FFB71B", "Wyndham": "#FFB71B",
  "Choice Hotels": "#E31837",
  "Best Western": "#003366", "Best Western Rewards": "#003366",
  "Accor": "#002C5F", "Accor Live Limitless": "#002C5F",
};

function ScoreBadge({ score }) {
  const tier = score >= 9 ? "exceptional" : score >= 8 ? "great" : score >= 7 ? "very-good" : "good";
  const label = { exceptional: "Exceptional", great: "Great", "very-good": "Very Good", good: "Recommended" }[tier];
  return (
    <div className="flex flex-col items-center text-center">
      <div className="bg-charcoal text-sand font-mono text-2xl md:text-3xl w-16 h-16 md:w-[72px] md:h-[72px] flex items-center justify-center">
        {score?.toFixed(1)}
      </div>
      <div className="overline text-charcoal/60 mt-1.5">{label}</div>
    </div>
  );
}

export default function HotelCard({ hotel, compact = false, variant = "grid", rank }) {
  const deals = hotel.deals || [];
  const sortedByPrice = [...deals].sort((a, b) => a.price - b.price);
  const bestDeal = sortedByPrice[0];
  const highest = sortedByPrice[sortedByPrice.length - 1];
  const savings = bestDeal && highest && highest.price > bestDeal.price ? Math.round(highest.price - bestDeal.price) : 0;
  const accent = BRAND_COLOR[bestDeal?.operator] || BRAND_COLOR[hotel.brand] || "#C86B53";

  // ---------------- LIST VARIANT (US News style) ----------------
  if (variant === "list") {
    return (
      <Link
        to={`/hotels/${hotel.id}`}
        data-testid={`hotel-card-${hotel.id}`}
        className="group relative grid grid-cols-1 md:grid-cols-12 bg-white border border-charcoal/10 hover:border-charcoal/30 transition-colors overflow-hidden"
      >
        {/* Rank ribbon */}
        {rank != null && (
          <div className="absolute top-0 left-0 z-10 bg-terracotta text-sand px-3 py-2 flex items-center gap-1.5">
            <span className="font-serif text-2xl leading-none">#{rank}</span>
          </div>
        )}

        {/* Image */}
        <div className="md:col-span-4 relative aspect-[5/4] md:aspect-auto md:min-h-[240px] overflow-hidden bg-stone">
          <img
            src={hotel.image}
            alt={hotel.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] group-hover:scale-[1.06]"
            loading="lazy"
          />
          {hotel.featured && (
            <span className="absolute bottom-3 left-3 bg-sand text-charcoal text-[10px] uppercase tracking-[0.2em] px-2.5 py-1">
              Editor's pick
            </span>
          )}
        </div>

        {/* Middle content */}
        <div className="md:col-span-5 p-6 md:p-7 md:border-r border-charcoal/10 flex flex-col justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-charcoal/60 mb-2">
              <span className="flex items-center gap-1"><MapPin size={11} /> {hotel.destination_name}</span>
              <span>·</span>
              <span className="flex items-center gap-0.5">
                {Array.from({ length: hotel.stars }).map((_, i) => (
                  <Star key={i} size={11} className="fill-gold text-gold" />
                ))}
              </span>
              <span>·</span>
              <span
                className="px-2 py-0.5 border-l-2 text-charcoal/80"
                style={{ borderLeftColor: accent }}
              >
                {hotel.brand}
              </span>
            </div>
            <h3 className="font-serif text-2xl md:text-3xl leading-tight text-charcoal group-hover:text-terracotta transition-colors">
              {hotel.name}
            </h3>
            <p className="mt-2 text-sm text-charcoal/70 line-clamp-2">{hotel.tagline}</p>
          </div>

          {hotel.amenities?.length > 0 && (
            <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5">
              {hotel.amenities.slice(0, 4).map((a) => (
                <li key={a} className="flex items-center gap-1.5 text-xs text-charcoal/75">
                  <Check size={11} className="text-terracotta" /> {a}
                </li>
              ))}
              {hotel.amenities.length > 4 && (
                <li className="text-xs text-charcoal/50">+{hotel.amenities.length - 4} more</li>
              )}
            </ul>
          )}
        </div>

        {/* Right: Score + price + CTA */}
        <div className="md:col-span-3 p-6 md:p-7 bg-cream/50 flex flex-col items-center justify-between gap-5">
          <ScoreBadge score={hotel.editor_score} />

          <div className="text-center">
            <div className="overline">Best price</div>
            <div className="font-serif text-3xl leading-none mt-1">
              ${Math.round(bestDeal?.price ?? hotel.price_from)}
            </div>
            <div className="text-[10px] text-charcoal/55 mt-1">/ night · taxes excl.</div>
            {bestDeal && (
              <div className="text-[11px] mt-1.5">
                via <span className="font-semibold" style={{ color: accent }}>{bestDeal.operator}</span>
              </div>
            )}
            {savings > 0 && (
              <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-forest font-semibold">
                <TrendingDown size={10} /> Save ${savings}
              </div>
            )}
          </div>

          <span className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-charcoal text-sand text-xs uppercase tracking-[0.18em] group-hover:bg-terracotta transition-colors">
            View deals <ArrowUpRight size={12} />
          </span>
        </div>
      </Link>
    );
  }

  // ---------------- GRID VARIANT (default — used on Home & DestinationDetail) ----------------
  return (
    <Link
      to={`/hotels/${hotel.id}`}
      data-testid={`hotel-card-${hotel.id}`}
      className="group relative block bg-white border border-charcoal/10 hover:border-charcoal/20 hover:-translate-y-1 transition-all duration-500 ease-out overflow-hidden"
    >
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-1 origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out"
        style={{ background: accent }}
      />
      <div className="relative aspect-[4/3] overflow-hidden bg-stone">
        <img
          src={hotel.image}
          alt={hotel.name}
          className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.08]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          <span
            className="bg-sand/95 backdrop-blur-sm text-charcoal text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border-l-2"
            style={{ borderLeftColor: accent }}
          >
            {hotel.brand}
          </span>
          {hotel.featured && (
            <span className="bg-terracotta text-sand text-[10px] uppercase tracking-[0.18em] px-2.5 py-1">
              Editor's pick
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3 bg-white text-charcoal text-xs font-mono px-2.5 py-1.5 shadow-md flex items-center gap-1">
          <Star size={10} className="fill-gold text-gold" />
          {hotel.editor_score?.toFixed(1)}
        </div>
        {savings > 0 && !compact && (
          <div className="absolute bottom-3 left-3 bg-forest text-sand text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 flex items-center gap-1.5">
            <TrendingDown size={11} /> Save ${savings}
          </div>
        )}
        {deals.length > 1 && (
          <div className="absolute bottom-3 right-3 flex -space-x-1">
            {deals.slice(0, 3).map((d, i) => (
              <span
                key={i}
                className="w-6 h-6 rounded-full ring-2 ring-white text-[9px] font-mono flex items-center justify-center text-sand"
                style={{ background: BRAND_COLOR[d.operator] || "#1A1A1A" }}
                title={d.operator}
              >
                {d.operator.replace(".com", "").slice(0, 2).toUpperCase()}
              </span>
            ))}
            {deals.length > 3 && (
              <span className="w-6 h-6 rounded-full ring-2 ring-white bg-charcoal text-sand text-[9px] font-mono flex items-center justify-center">
                +{deals.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="p-5 md:p-6">
        <div className="flex items-center gap-1.5 text-xs text-charcoal/60 mb-2">
          <MapPin size={12} /> <span>{hotel.destination_name}</span>
          <span className="mx-1.5">·</span>
          <span className="flex items-center gap-0.5">
            {Array.from({ length: hotel.stars }).map((_, i) => (
              <Star key={i} size={11} className="fill-gold text-gold" />
            ))}
          </span>
        </div>
        <h3 className="font-serif text-2xl leading-tight text-charcoal group-hover:text-terracotta transition-colors">
          {hotel.name}
        </h3>
        {!compact && <p className="mt-2 text-sm text-charcoal/70 line-clamp-2">{hotel.tagline}</p>}
        <div className="mt-5 pt-5 border-t border-charcoal/10 flex items-end justify-between gap-4">
          <div>
            <div className="overline">Best price tonight</div>
            <div className="font-serif text-3xl leading-none mt-1">
              ${Math.round(bestDeal?.price ?? hotel.price_from)}
              <span className="text-xs text-charcoal/50 font-sans ml-1">/ night</span>
            </div>
            {bestDeal && (
              <div className="mt-1.5 text-[11px] text-charcoal/60 inline-flex items-center gap-1">
                via <span className="font-semibold" style={{ color: accent }}>{bestDeal.operator}</span>
              </div>
            )}
          </div>
          <div className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-charcoal group-hover:text-terracotta whitespace-nowrap">
            Compare <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>
      </div>
    </Link>
  );
}
